import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { serviceRequestService } from '../../services/serviceRequestService';
import { testimonialService } from '../../services/testimonialService';
import { scheduleService } from '../../services/scheduleService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const DIAGNOSTIC_PREFIX = 'DIAGNOSTIC_PAYLOAD::';

const AdminDashboard = () => {
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user is admin
    if (userProfile && userProfile?.role !== 'admin') {
      navigate('/customer/dashboard');
      return;
    }

    loadData();
  }, [userProfile, navigate]);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [requestsResult, testimonialsResult, schedulesResult] = await Promise.all([
        serviceRequestService?.getAllRequests(),
        testimonialService?.getAll(),
        scheduleService?.getAllSchedules()
      ]);

      if (requestsResult?.error) throw requestsResult?.error;
      if (testimonialsResult?.error) throw testimonialsResult?.error;
      if (schedulesResult?.error) throw schedulesResult?.error;

      setRequests(requestsResult?.data || []);
      setTestimonials(testimonialsResult?.data || []);
      setSchedules(schedulesResult?.data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      const { error } = await serviceRequestService?.updateStatus(requestId, newStatus);
      if (error) throw error;
      
      // Update local state
      setRequests(prev => prev?.map(req => 
        req?.id === requestId ? { ...req, status: newStatus } : req
      ));
    } catch (err) {
      alert('Failed to update status: ' + err?.message);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!confirm('Are you sure you want to delete this request?')) return;

    try {
      const { error } = await serviceRequestService?.delete(requestId);
      if (error) throw error;
      
      setRequests(prev => prev?.filter(req => req?.id !== requestId));
    } catch (err) {
      alert('Failed to delete request: ' + err?.message);
    }
  };

  const handleTogglePublish = async (testimonialId, currentStatus) => {
    try {
      const { error } = await testimonialService?.update(testimonialId, {
        isPublished: !currentStatus
      });
      if (error) throw error;
      
      setTestimonials(prev => prev?.map(test => 
        test?.id === testimonialId ? { ...test, is_published: !currentStatus } : test
      ));
    } catch (err) {
      alert('Failed to update testimonial: ' + err?.message);
    }
  };

  const handleDeleteTestimonial = async (testimonialId) => {
    if (!confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      const { error } = await testimonialService?.delete(testimonialId);
      if (error) throw error;
      
      setTestimonials(prev => prev?.filter(test => test?.id !== testimonialId));
    } catch (err) {
      alert('Failed to delete testimonial: ' + err?.message);
    }
  };

  const handleScheduleStatusUpdate = async (scheduleId, newStatus) => {
    try {
      const { error } = await scheduleService?.updateStatus(scheduleId, newStatus);
      if (error) throw error;
      
      setSchedules(prev => prev?.map(sch => 
        sch?.id === scheduleId ? { ...sch, status: newStatus } : sch
      ));
    } catch (err) {
      alert('Failed to update schedule status: ' + err?.message);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!confirm('Are you sure you want to delete this scheduled service?')) return;

    try {
      const { error } = await scheduleService?.delete(scheduleId);
      if (error) throw error;
      
      setSchedules(prev => prev?.filter(sch => sch?.id !== scheduleId));
    } catch (err) {
      alert('Failed to delete schedule: ' + err?.message);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning text-warning-foreground',
      contacted: 'bg-blue-500 text-white',
      scheduled: 'bg-purple-500 text-white',
      completed: 'bg-success text-success-foreground',
      cancelled: 'bg-error text-error-foreground'
    };
    return colors?.[status] || 'bg-gray-500 text-white';
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      emergency: 'text-error',
      urgent: 'text-warning',
      normal: 'text-blue-600',
      flexible: 'text-muted-foreground'
    };
    return colors?.[urgency] || 'text-foreground';
  };

  const parseDiagnosticPayload = (message) => {
    if (!message || typeof message !== 'string' || !message?.startsWith(DIAGNOSTIC_PREFIX)) {
      return null;
    }

    try {
      return JSON.parse(message?.slice(DIAGNOSTIC_PREFIX?.length));
    } catch {
      return null;
    }
  };

  const getScheduleStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning text-warning-foreground',
      confirmed: 'bg-blue-500 text-white',
      completed: 'bg-success text-success-foreground',
      cancelled: 'bg-error text-error-foreground'
    };
    return colors?.[status] || 'bg-gray-500 text-white';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="font-body text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="Wrench" size={32} color="var(--color-primary)" />
              <div>
                <h1 className="font-headline font-bold text-xl text-foreground">Admin Dashboard</h1>
                <p className="font-body text-sm text-muted-foreground">meriodine</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden md:block">
                <p className="font-body text-sm font-medium text-foreground">{userProfile?.full_name}</p>
                <p className="font-body text-xs text-muted-foreground">{userProfile?.email}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleSignOut}>
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>
      {/* Tabs */}
      <div className="bg-card border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-6 py-3 font-body font-medium text-sm transition-colors ${
                activeTab === 'requests' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Service Requests ({requests?.length})
            </button>
            <button
              onClick={() => setActiveTab('schedules')}
              className={`px-6 py-3 font-body font-medium text-sm transition-colors ${
                activeTab === 'schedules' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Scheduled Services ({schedules?.length})
            </button>
            <button
              onClick={() => setActiveTab('testimonials')}
              className={`px-6 py-3 font-body font-medium text-sm transition-colors ${
                activeTab === 'testimonials' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Testimonials ({testimonials?.length})
            </button>
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="bg-error/10 border border-error text-error rounded-lg p-4 mb-6">
            {error}
          </div>
        )}

        {/* Service Requests Tab */}
        {activeTab === 'requests' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-headline font-bold text-2xl text-foreground">Service Requests</h2>
              <Button variant="outline" size="sm" onClick={loadData}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Refresh
              </Button>
            </div>

            {requests?.length === 0 ? (
              <div className="bg-card rounded-lg border border-border p-12 text-center">
                <Icon name="Inbox" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <p className="font-body text-muted-foreground">No service requests yet</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {requests?.map((request) => {
                  const diagnosticPayload = parseDiagnosticPayload(request?.message);
                  const issue = diagnosticPayload?.issue;
                  const media = diagnosticPayload?.media;

                  return (
                  <div key={request?.id} className="bg-card rounded-lg border border-border p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-headline font-bold text-lg text-foreground">{request?.name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request?.status)}`}>
                            {request?.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm font-body">
                          <p><span className="text-muted-foreground">Phone:</span> <span className="font-medium">{request?.phone}</span></p>
                          {request?.email && <p><span className="text-muted-foreground">Email:</span> {request?.email}</p>}
                          <p><span className="text-muted-foreground">Service:</span> {request?.service_type}</p>
                          <p className={getUrgencyColor(request?.urgency)}>
                            <span className="text-muted-foreground">Urgency:</span> <span className="font-medium">{request?.urgency}</span>
                          </p>
                          {request?.preferred_time && <p><span className="text-muted-foreground">Preferred Time:</span> {request?.preferred_time}</p>}
                          <p><span className="text-muted-foreground">Submitted:</span> {new Date(request.created_at)?.toLocaleDateString()}</p>
                        </div>
                        {diagnosticPayload && (
                          <div className="mt-3 p-3 bg-accent/10 rounded space-y-3">
                            <p className="text-sm font-body text-foreground font-semibold">
                              Structured diagnostic intake received
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm font-body">
                              <p><span className="text-muted-foreground">Location:</span> {issue?.locationLabel || issue?.location || '-'}</p>
                              <p><span className="text-muted-foreground">Issue Type:</span> {issue?.issueTypeLabel || issue?.issueType || '-'}</p>
                              {issue?.startedAt && <p><span className="text-muted-foreground">Started:</span> {new Date(issue?.startedAt)?.toLocaleString()}</p>}
                              {issue?.affectedFixtures && <p><span className="text-muted-foreground">Affected:</span> {issue?.affectedFixtures}</p>}
                            </div>
                            {issue?.detailedDescription && (
                              <div className="bg-background/70 rounded p-3">
                                <p className="text-sm font-body text-foreground">{issue?.detailedDescription}</p>
                              </div>
                            )}
                            {Array.isArray(issue?.hazards) && issue?.hazards?.length > 0 && (
                              <p className="text-sm font-body text-error">
                                <span className="font-medium">Hazards:</span> {issue?.hazards?.join(', ')}
                              </p>
                            )}

                            {Array.isArray(media?.photos) && media?.photos?.length > 0 && (
                              <div>
                                <p className="text-sm font-medium text-foreground mb-2">Photos</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                  {media?.photos?.map((photo, index) => (
                                    <a
                                      key={`${photo?.storagePath || photo?.publicUrl || index}`}
                                      href={photo?.publicUrl || '#'}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="block border border-border rounded overflow-hidden bg-background"
                                    >
                                      {photo?.publicUrl ? (
                                        <img
                                          src={photo?.publicUrl}
                                          alt={`Diagnostic photo ${index + 1}`}
                                          className="w-full h-24 object-cover"
                                        />
                                      ) : (
                                        <div className="h-24 flex items-center justify-center text-xs text-muted-foreground px-2 text-center">
                                          Uploaded (no public URL): {photo?.fileName || 'photo'}
                                        </div>
                                      )}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}

                            {media?.voice && (
                              <div>
                                <p className="text-sm font-medium text-foreground mb-2">Voice Note</p>
                                {media?.voice?.publicUrl ? (
                                  <audio controls src={media?.voice?.publicUrl} className="w-full" />
                                ) : (
                                  <p className="text-xs text-muted-foreground">
                                    Voice uploaded without public URL: {media?.voice?.fileName || media?.voice?.storagePath}
                                  </p>
                                )}
                              </div>
                            )}

                            {Array.isArray(media?.uploadErrors) && media?.uploadErrors?.length > 0 && (
                              <div className="bg-warning/10 border border-warning/30 rounded p-2">
                                <p className="text-xs font-medium text-warning">Upload warnings:</p>
                                <p className="text-xs text-warning">{media?.uploadErrors?.join(' | ')}</p>
                              </div>
                            )}
                          </div>
                        )}
                        {!diagnosticPayload && request?.message && (
                          <div className="mt-3 p-3 bg-accent/10 rounded">
                            <p className="text-sm font-body text-foreground">{request?.message}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                      <select
                        value={request?.status}
                        onChange={(e) => handleStatusUpdate(request?.id, e?.target?.value)}
                        className="px-3 py-1.5 text-sm border border-input rounded bg-background"
                      >
                        <option value="pending">Pending</option>
                        <option value="contacted">Contacted</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.location.href = `tel:${request?.phone}`}
                      >
                        <Icon name="Phone" size={14} className="mr-1" />
                        Call
                      </Button>
                      {request?.email && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.location.href = `mailto:${request?.email}`}
                        >
                          <Icon name="Mail" size={14} className="mr-1" />
                          Email
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteRequest(request?.id)}
                      >
                        <Icon name="Trash2" size={14} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                );
                })}
              </div>
            )}
          </div>
        )}

        {/* Scheduled Services Tab */}
        {activeTab === 'schedules' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-headline font-bold text-2xl text-foreground">Scheduled Services</h2>
              <Button variant="outline" size="sm" onClick={loadData}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Refresh
              </Button>
            </div>

            {schedules?.length === 0 ? (
              <div className="bg-card rounded-lg border border-border p-12 text-center">
                <Icon name="Calendar" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <p className="font-body text-muted-foreground">No scheduled services yet</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {schedules?.map((schedule) => (
                  <div key={schedule?.id} className="bg-card rounded-lg border border-border p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-headline font-bold text-lg text-foreground">{schedule?.name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getScheduleStatusColor(schedule?.status)}`}>
                            {schedule?.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm font-body">
                          <p><span className="text-muted-foreground">Phone:</span> <span className="font-medium">{schedule?.phone}</span></p>
                          {schedule?.email && <p><span className="text-muted-foreground">Email:</span> {schedule?.email}</p>}
                          <p><span className="text-muted-foreground">Service:</span> {schedule?.service_type}</p>
                          <p className="text-primary font-medium">
                            <span className="text-muted-foreground">Scheduled:</span> {new Date(schedule?.scheduled_date)?.toLocaleDateString()} - {schedule?.scheduled_time}
                          </p>
                          <p><span className="text-muted-foreground">Booked:</span> {new Date(schedule?.created_at)?.toLocaleDateString()}</p>
                        </div>
                        {schedule?.message && (
                          <div className="mt-3 p-3 bg-accent/10 rounded">
                            <p className="text-sm font-body text-foreground">{schedule?.message}</p>
                          </div>
                        )}
                        {schedule?.admin_notes && (
                          <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200">
                            <p className="text-xs text-muted-foreground mb-1">Admin Notes:</p>
                            <p className="text-sm font-body text-foreground">{schedule?.admin_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                      <select
                        value={schedule?.status}
                        onChange={(e) => handleScheduleStatusUpdate(schedule?.id, e?.target?.value)}
                        className="px-3 py-1.5 text-sm border border-input rounded bg-background"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteSchedule(schedule?.id)}
                      >
                        <Icon name="Trash2" size={14} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-headline font-bold text-2xl text-foreground">Testimonials</h2>
              <Button variant="outline" size="sm" onClick={loadData}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Refresh
              </Button>
            </div>

            {testimonials?.length === 0 ? (
              <div className="bg-card rounded-lg border border-border p-12 text-center">
                <Icon name="MessageSquare" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <p className="font-body text-muted-foreground">No testimonials yet</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {testimonials?.map((testimonial) => (
                  <div key={testimonial?.id} className="bg-card rounded-lg border border-border p-6">
                    <div className="flex items-start gap-4">
                      {testimonial?.image && (
                        <img
                          src={testimonial?.image}
                          alt={testimonial?.client_name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h3 className="font-headline font-bold text-lg text-foreground">{testimonial?.client_name}</h3>
                            <p className="font-body text-sm text-muted-foreground">{testimonial?.client_role} • {testimonial?.location}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              testimonial?.is_published ? 'bg-success text-success-foreground' : 'bg-gray-500 text-white'
                            }`}>
                              {testimonial?.is_published ? 'Published' : 'Draft'}
                            </span>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex">
                              {[...Array(testimonial?.rating)]?.map((_, i) => (
                                <Icon key={i} name="Star" size={16} className="text-warning fill-current" />
                              ))}
                            </div>
                            <span className="px-2 py-0.5 bg-accent/20 text-accent rounded text-xs font-medium">
                              {testimonial?.category}
                            </span>
                          </div>
                          <p className="font-body text-sm font-medium text-foreground mb-1">{testimonial?.problem}</p>
                          <p className="font-body text-sm text-muted-foreground line-clamp-2">{testimonial?.testimonial_text}</p>
                        </div>
                        <div className="flex flex-wrap gap-2 pt-3 border-t border-border">
                          <Button
                            variant={testimonial?.is_published ? 'outline' : 'default'}
                            size="sm"
                            onClick={() => handleTogglePublish(testimonial?.id, testimonial?.is_published)}
                          >
                            <Icon name={testimonial?.is_published ? 'EyeOff' : 'Eye'} size={14} className="mr-1" />
                            {testimonial?.is_published ? 'Unpublish' : 'Publish'}
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteTestimonial(testimonial?.id)}
                          >
                            <Icon name="Trash2" size={14} className="mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
